// SPDX-License-Identifier: AGPL-3.0-or-later

import {openGuildBoostSlotPicker} from '@app/features/boost/components/GuildBoostSlotPicker';
import GuildBoosts from '@app/features/boost/state/GuildBoosts';
import {boostCooldownUntil, boostFailure, freeBoostSlots} from '@app/features/boost/utils/GuildBoostPurchase';
import {showGuildErrorModal} from '@app/features/guild/components/alerts/GuildErrorModalUtils';
import {
	SOMETHING_WENT_WRONG_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {getFormattedDateTime} from '@app/features/user/utils/DateFormatting';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useRef, useState} from 'react';

const SLOT_GONE_DESCRIPTOR = msg({
	message: 'This boost slot has ended or was refunded, so it can no longer boost.',
	comment: 'Error after boosting a community with a bought boost slot that ended or was refunded meanwhile.',
});
const SLOT_BUSY_DESCRIPTOR = msg({
	message:
		"This boost slot can't boost right now. A slot that moved to another community in the last 7 days has to wait.",
	comment:
		'Error after boosting a community with a slot the server refused as in use: on another community, moved within the 7 day wait, or taken by a request at the same moment.',
});

const SLOT_COOLING_TITLE_DESCRIPTOR = msg({
	message: 'Boost slot is waiting',
	comment: 'Title of the error after boosting with a slot that was put on a community less than 7 days ago.',
});
const SLOT_COOLING_UNTIL_DESCRIPTOR = msg({
	message:
		"You can use this slot again on {date}. A boost slot can't be put on any community for 7 days after it's put on one.",
	comment:
		'Error after boosting with a slot that was put on a community less than 7 days ago. {date} is the local date and time the wait ends.',
});
const SLOT_COOLING_DESCRIPTOR = msg({
	message: "A boost slot can't be put on any community for 7 days after it's put on one.",
	comment:
		'Error after boosting with a slot that was put on a community less than 7 days ago, when the end of the wait is unknown.',
});

export function useGuildBoostAction() {
	const {i18n} = useLingui();
	const [acting, setActing] = useState(false);
	const inFlight = useRef(false);
	const showError = useCallback(
		(message: MessageDescriptor = TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR) => {
			showGuildErrorModal({
				title: i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR),
				message: i18n._(message),
				dataFlx: 'boost.action.error-modal',
			});
		},
		[i18n],
	);
	const boostWith = useCallback(
		async (slotId?: string): Promise<boolean> => {
			if (inFlight.current) return false;
			inFlight.current = true;
			setActing(true);
			try {
				return await GuildBoosts.boost(slotId);
			} catch (error) {
				const failure = boostFailure(error);
				// The slot list on screen is stale: the slot ended, was refunded or is taken.
				if (failure) void GuildBoosts.loadSlots();
				if (failure === 'slot_cooling') {
					const until = boostCooldownUntil(error);
					showGuildErrorModal({
						title: i18n._(SLOT_COOLING_TITLE_DESCRIPTOR),
						message:
							until === null
								? i18n._(SLOT_COOLING_DESCRIPTOR)
								: i18n._(SLOT_COOLING_UNTIL_DESCRIPTOR, {date: getFormattedDateTime(until)}),
						dataFlx: 'boost.action.error-modal',
					});
					return false;
				}
				showError(
					failure === 'slot_gone'
						? SLOT_GONE_DESCRIPTOR
						: failure === 'slot_busy'
							? SLOT_BUSY_DESCRIPTOR
							: TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
				);
				return false;
			} finally {
				inFlight.current = false;
				setActing(false);
			}
		},
		[i18n, showError],
	);
	// With a bought slot among several free ones, the member picks which; otherwise the one that ends first.
	const handleBoost = useCallback(async (): Promise<boolean> => {
		const free = freeBoostSlots(GuildBoosts.slots, Date.now());
		if (free.length > 1 && free.some((slot) => slot.expires_at != null)) {
			openGuildBoostSlotPicker(free, boostWith);
			return false;
		}
		return boostWith();
	}, [boostWith]);
	return {acting, handleBoost, showError};
}
